import { memo } from 'react';
import { useAppSelector } from '../_hooks/useAppSelector';
import {
  formatMeasurementNumber,
  getPhysicalSizes,
  getPhysicalSizeUnit,
} from './SizeDisplay';
import type { Locale } from '../../UserData/_stores/UserPrefsStore';

interface BaseProps {
  maxSizes: number[]
  minSizes: number[]
  separator?: string
}

interface PixelPhysicalSizeRangeDisplayProps extends BaseProps {
  displayType: 'physical'
  sourceUnit: 'px'
  dpi: number
}

interface UnitPhysicalSizeRangeDisplayProps extends BaseProps {
  displayType: 'physical'
  sourceUnit: 'in' | 'cm'
  dpi?: never
}

interface DigitalSizeRangeDisplayProps extends BaseProps {
  displayType: 'digital'
  sourceUnit: 'px'
  dpi?: never
}

export type SizeRangeDisplayProps =
  | PixelPhysicalSizeRangeDisplayProps
  | UnitPhysicalSizeRangeDisplayProps
  | DigitalSizeRangeDisplayProps;

const getSizeCount = (minSizes: number[], maxSizes: number[]) => {
  return Math.max(minSizes.length, maxSizes.length);
}

const hasSizeAtIndex = (sizes: number[], index: number) => {
  return Object.prototype.hasOwnProperty.call(sizes, index);
}

const getRangeValue = (sizes: number[], fallbackSizes: number[], index: number) => {
  if (hasSizeAtIndex(sizes, index)) {
    return sizes[index];
  }

  if (hasSizeAtIndex(fallbackSizes, index)) {
    return fallbackSizes[index];
  }

  return 0;
}

interface FormatRangeOptions {
  locale: Locale
  maxSizes: number[]
  maximumFractionDigits: number
  minSizes: number[]
  separator: string
}

const formatRange = (options: FormatRangeOptions) => {
  return Array.from({ length: getSizeCount(options.minSizes, options.maxSizes) }, (_item, index) => {
    const minSize = formatMeasurementNumber(
      getRangeValue(options.minSizes, options.maxSizes, index),
      options.locale,
      options.maximumFractionDigits
    );
    const maxSize = formatMeasurementNumber(
      getRangeValue(options.maxSizes, options.minSizes, index),
      options.locale,
      options.maximumFractionDigits
    );

    return minSize === maxSize ? minSize : minSize + options.separator + maxSize;
  }).join(' x ');
}

const formatDigitalSizeRange = (
  minSizes: number[],
  maxSizes: number[],
  locale: Locale,
  separator: string
) => {
  const value = formatRange({
    locale,
    maximumFractionDigits: 0,
    minSizes: minSizes.map(value => Math.round(value)),
    maxSizes: maxSizes.map(value => Math.round(value)),
    separator,
  });

  return getSizeCount(minSizes, maxSizes) === 1 ? value + 'px' : value + ' px';
}

function SizeRangeDisplay(props: SizeRangeDisplayProps) {
  const locale = useAppSelector(state => state.get('userData').get('prefs').get('lang'));
  const measurementSystem = useAppSelector(state => state.get('userData').get('prefs').get('measurementSystem'));
  const separator = props.separator ?? '-';

  if (props.displayType === 'digital') {
    return formatDigitalSizeRange(props.minSizes, props.maxSizes, locale, separator);
  }

  const minSizes = getPhysicalSizes({
    sizes: props.minSizes,
    sourceUnit: props.sourceUnit,
    measurementSystem,
    locale,
    dpi: props.dpi,
  });
  const maxSizes = getPhysicalSizes({
    sizes: props.maxSizes,
    sourceUnit: props.sourceUnit,
    measurementSystem,
    locale,
    dpi: props.dpi,
  });

  if (minSizes === undefined || maxSizes === undefined) {
    return formatDigitalSizeRange(props.minSizes, props.maxSizes, locale, separator);
  }

  return formatRange({
    locale,
    maxSizes,
    maximumFractionDigits: 2,
    minSizes,
    separator,
  }) + ' ' + getPhysicalSizeUnit(measurementSystem);
}

export default memo(SizeRangeDisplay);
